import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import debounce from "lodash.debounce";
import useStockHistory from "./hooks/useStockHistory";
import useIntraday from "./hooks/useIntraday";
import PriceChart from "./components/PriceChart";
import LivePrice from "./components/LivePrice";
import PredictionBanner from "./components/PredictionBanner";
import TopGainers from "./components/TopGainers";
import { longDate, sessionLabel, isToday, money } from "./utils/format";
import "./App.css";

const POPULAR = ["AAPL", "MSFT", "NVDA", "TSLA", "AMZN", "GOOGL"];
const RANGES = [
  { key: "1D", intraday: true, unit: "hour" },
  { key: "1W", days: 5, unit: "day" },
  { key: "1M", days: 21, unit: "day" },
  { key: "3M", days: 63, unit: "month" },
  { key: "6M", days: 126, unit: "month" },
  { key: "1Y", days: Infinity, unit: "month" },
];


// A Finnhub key is required: validate it by making a real (cheap) request.
async function validateFinnhubKey(key) {
  try {
    const res = await axios.get(
      `https://finnhub.io/api/v1/quote?symbol=AAPL&token=${key}`
    );
    return res.status === 200 && res.data && typeof res.data.c === "number";
  } catch (err) {
    return false;
  }
}

function App() {
  const initialSymbol = localStorage.getItem("last_symbol") || "AAPL";
  const [input, setInput] = useState(initialSymbol);
  const [refreshCount, setRefreshCount] = useState(0);
  const [symbol, setSymbol] = useState(initialSymbol);
  const [errorMsg, setErrorMsg] = useState(null);
  const [range, setRange] = useState("1Y");
  const [view, setView] = useState(
    () => (window.location.hash === "#gainers" ? "gainers" : "dashboard")
  ); // "dashboard" | "gainers"
  const searchRef = useRef(null);

  // Auth gate: the app is unavailable until a valid Finnhub key is provided.
  const [apiKey, setApiKey] = useState(
    () => localStorage.getItem("finnhub_api_key") || process.env.REACT_APP_FINNHUB_API_KEY || ""
  );
  const [authed, setAuthed] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [checking, setChecking] = useState(false);
  const [keyError, setKeyError] = useState(null);

  const { data, loading, error } = useStockHistory(
    authed ? symbol : null,
    refreshCount
  );
  const isIntraday = range === "1D";
  const intraday = useIntraday(authed ? symbol : null, isIntraday, refreshCount);

  // Re-validate a previously saved key on load.
  useEffect(() => {
    if (!apiKey) return;
    let cancelled = false;
    setChecking(true);
    validateFinnhubKey(apiKey).then((ok) => {
      if (cancelled) return;
      setChecking(false);
      if (ok) {
        setAuthed(true);
      } else {
        localStorage.removeItem("finnhub_api_key");
        setApiKey("");
        setKeyError("Your saved key is no longer valid — please enter it again.");
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Symbol validation (only relevant once authed).
  useEffect(() => {
    if (!authed) return;
    const query = input.trim().toUpperCase();
    if (!query) return;

    const validate = async () => {
      try {
        const res = await axios.get(
          `https://finnhub.io/api/v1/search?q=${query}&token=${apiKey}`
        );
        const exact = res.data.result.find((item) => item.symbol === query);
        if (exact) {
          setErrorMsg(null);
          setSymbol(query);
        } else {
          setErrorMsg("Invalid symbol");
        }
      } catch (err) {
        console.error(err);
        setErrorMsg("Validation error or rate limit");
      }
    };

    const debounced = debounce(validate, 500);
    debounced();
    return () => debounced.cancel();
  }, [input, refreshCount, apiKey, authed]);

  // Persist the current symbol so it's restored on reload.
  useEffect(() => {
    if (!authed || !symbol) return;
    localStorage.setItem("last_symbol", symbol);
  }, [symbol, authed]);

  // Live document title.
  useEffect(() => {
    document.title = authed ? `${symbol} · Stock Visual` : "Stock Visual";
  }, [symbol, authed]);

  // "/" focuses search.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && document.activeElement !== searchRef.current) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Browser back/forward between sections, driven by the URL hash.
  useEffect(() => {
    const onHash = () =>
      setView(window.location.hash === "#gainers" ? "gainers" : "dashboard");
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Navigate by updating the hash so it creates a history entry.
  const goTo = (v) => {
    window.location.hash = v === "gainers" ? "gainers" : "";
  };

  const selectSymbol = (sym) => {
    setInput(sym);
    setSymbol(sym);
    setErrorMsg(null);
  };

  // Active data source: intraday for 1D, otherwise the daily 1Y series.
  const chartSource = isIntraday ? intraday.data : data;
  const chartLoading = isIntraday ? intraday.loading : loading;
  const chartError = isIntraday ? intraday.error : null;
  const chartUnit = RANGES.find((r) => r.key === range)?.unit ?? "month";

  // Range-filtered series + period stats.
  const { series, stats } = useMemo(() => {
    if (!Array.isArray(chartSource) || chartSource.length === 0) {
      return { series: [], stats: null };
    }
    let series;
    if (isIntraday) {
      series = chartSource;
    } else {
      const days = RANGES.find((r) => r.key === range)?.days ?? Infinity;
      series = days === Infinity ? chartSource : chartSource.slice(-days);
    }
    const closes = series.map((d) => d.Close).filter((n) => typeof n === "number");
    if (closes.length === 0) return { series, stats: null };
    const first = closes[0];
    const last = closes[closes.length - 1];
    const high = Math.max(...series.map((d) => d.High ?? d.Close));
    const low = Math.min(...series.map((d) => d.Low ?? d.Close));
    const changePct = ((last - first) / first) * 100;
    return { series, stats: { last, high, low, changePct } };
  }, [chartSource, range, isIntraday]);

  // For the 1D view: if the latest intraday bar isn't from today, the market
  // is closed and we're showing the last session — surface that to the user.
  const sessionStaleDate = useMemo(() => {
    if (!isIntraday || series.length === 0) return null;
    const lastBar = new Date(series[series.length - 1].Date);
    return isToday(lastBar) ? null : lastBar;
  }, [isIntraday, series]);

  const submitKey = async (e) => {
    e.preventDefault();
    const key = keyInput.trim();
    if (!key) {
      setKeyError("Please paste your Finnhub API key.");
      return;
    }
    setChecking(true);
    setKeyError(null);
    const ok = await validateFinnhubKey(key);
    setChecking(false);
    if (ok) {
      localStorage.setItem("finnhub_api_key", key);
      setApiKey(key);
      setAuthed(true);
    } else {
      setKeyError("That key didn't work. Check it and try again.");
    }
  };

  const changeKey = () => {
    localStorage.removeItem("finnhub_api_key");
    setApiKey("");
    setKeyInput("");
    setAuthed(false);
    setKeyError(null);
  };

  /* ----------------------------- Key gate ----------------------------- */
  if (!authed) {
    const verifyingSaved = checking && !!apiKey;
    return (
      <div className="gate">
        <div className="gate-card fade-in">
          <span className="brand-name">
            Stock<em>Visual</em>
          </span>
          <div className="gate-rule" />
          <p>
            {verifyingSaved
              ? "Verifying your key…"
              : "Enter a Finnhub API key to continue."}
          </p>

          {verifyingSaved ? (
            <div className="state">
              <span className="spinner" /> Checking…
            </div>
          ) : (
            <form className="gate-form" onSubmit={submitKey}>
              <input
                type="password"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder="Paste your Finnhub API key"
                autoFocus
              />
              {keyError && <span className="gate-error">{keyError}</span>}
              <button className="btn" type="submit" disabled={checking}>
                {checking ? (
                  <>
                    <span className="spinner" /> Verifying…
                  </>
                ) : (
                  "Unlock"
                )}
              </button>
            </form>
          )}

          <div className="gate-hint">
            No key?{" "}
            <a href="https://finnhub.io/register" target="_blank" rel="noreferrer">
              Get one free
            </a>
            . Stored only in your browser.
          </div>
        </div>
        <span className="credit">Created by Gagandeep Malhotra</span>
      </div>
    );
  }

  /* ----------------------------- Dashboard ----------------------------- */
  const today = longDate(new Date());

  return (
    <div className="app">
      <header className="topbar">
        <span className="brand-name">
          Stock<em>Visual</em>
        </span>
        <nav className="nav-tabs">
          <button
            className={`nav-tab${view === "dashboard" ? " active" : ""}`}
            onClick={() => goTo("dashboard")}
          >
            Dashboard
          </button>
          <button
            className={`nav-tab${view === "gainers" ? " active" : ""}`}
            onClick={() => goTo("gainers")}
          >
            Top Gainers
          </button>
        </nav>
        <div className="topbar-actions">
          {view === "dashboard" && (
            <span className="topbar-ticker">
              Tracking <strong>{symbol}</strong>
            </span>
          )}
          <button className="nav-btn" onClick={changeKey}>
            Change key
          </button>
        </div>
      </header>

      <main className="container">
        {view === "gainers" ? (
          <TopGainers
            active={view === "gainers"}
            onSelect={(sym) => {
              selectSymbol(sym);
              goTo("dashboard");
            }}
          />
        ) : (
        <>
        <div className="masthead">
          <div className="dateline">
            <span className="kicker">Daily Market Brief</span>
            <span className="kicker dateline-sep">·</span>
            <span className="kicker">{today}</span>
          </div>
          <h1>Stock charts and predictions</h1>
          <p className="lede">
            Search a ticker to see its past year of prices and a next-day
            up or down prediction.
          </p>
        </div>

        <div className="toolbar">
          <div className="search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
              <path d="M20 20l-3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              ref={searchRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value.toUpperCase())}
              placeholder="Search a symbol (e.g. NVDA)"
            />
            <span className="kbd">/</span>
          </div>

          <button
            className={`btn-icon${loading ? " spinning" : ""}`}
            onClick={() => setRefreshCount((c) => c + 1)}
            title="Refresh"
            aria-label="Refresh data"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M20 11a8 8 0 10-1.5 5M20 5v6h-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {errorMsg && <span className="badge--error">{errorMsg}</span>}
        </div>

        <div className="quickpicks">
          <span className="qp-label">Popular</span>
          {POPULAR.map((s) => (
            <button
              key={s}
              className={`quickpick${s === symbol ? " active" : ""}`}
              onClick={() => selectSymbol(s)}
            >
              {s}
            </button>
          ))}
        </div>

        {error && !loading ? (
          <div className="panel panel-pad">
            <div className="state state--error">
              {error}
              <button className="retry" onClick={() => setRefreshCount((c) => c + 1)}>
                Retry
              </button>
            </div>
          </div>
        ) : (
          <div className="grid fade-in" key={symbol}>
            <PredictionBanner symbol={symbol} />

            <div className="panel panel-pad">
              <div className="panel-head">
                <h2 className="kicker">Close price</h2>
                <div className="ranges">
                  {RANGES.map((r) => (
                    <button
                      key={r.key}
                      className={`range-btn${range === r.key ? " active" : ""}`}
                      onClick={() => setRange(r.key)}
                    >
                      {r.key}
                    </button>
                  ))}
                </div>
              </div>

              {sessionStaleDate && !chartLoading && !chartError && (
                <div className="market-note">
                  Market closed — showing the last session ({sessionLabel(sessionStaleDate)}).
                </div>
              )}

              {chartLoading ? (
                <div className="chart-wrap skeleton" />
              ) : chartError ? (
                <div className="state state--error">
                  {chartError}
                  <button className="retry" onClick={() => setRefreshCount((c) => c + 1)}>
                    Retry
                  </button>
                </div>
              ) : (
                <>
                  <PriceChart
                    data={series}
                    trend={stats ? stats.changePct >= 0 : true}
                    unit={chartUnit}
                  />
                  {stats && (
                    <div className="stats-row">
                      <div className="stat">
                        <div className="stat-label">{range} change</div>
                        <div
                          className={`stat-value mono ${stats.changePct >= 0 ? "up-text" : "down-text"}`}
                        >
                          {stats.changePct >= 0 ? "+" : ""}
                          {stats.changePct.toFixed(2)}%
                        </div>
                      </div>
                      <div className="stat">
                        <div className="stat-label">Period high</div>
                        <div className="stat-value mono">{money(stats.high)}</div>
                      </div>
                      <div className="stat">
                        <div className="stat-label">Period low</div>
                        <div className="stat-value mono">{money(stats.low)}</div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="side-stack">
              <div className="panel panel-pad">
                <div className="panel-head">
                  <h2 className="kicker">Live price</h2>
                  <span className="meta">{symbol}</span>
                </div>
                <LivePrice symbol={symbol} apiKey={apiKey} />
              </div>
            </div>
          </div>
        )}
        </>
        )}
      </main>
      <span className="credit">Created by Gagandeep Malhotra</span>
    </div>
  );
}

export default App;
